import React, { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BadgeCelebrationsProvider } from './contexts/BadgeCelebrationsContext';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import LiveTicker from './components/common/LiveTicker';
import { PageLoader } from './components/common/LoadingSpinner';
import { ProGate } from './components/common/ProGate';
import StaffPreviewBanner from './components/common/StaffPreviewBanner';
import MemberAtmosphere from './components/layout/MemberAtmosphere';
import { ROLES, ROLE_DASHBOARDS } from './lib/roles';

// Pages — code-split via React.lazy so each route's bundle is fetched on
// demand. Keeps the initial JS payload small; the shared <React.Suspense>
// wrapper below shows <PageLoader /> during chunk load.
const HomePage = React.lazy(() => import('./pages/HomePage'));
const PublicProspectPage = React.lazy(() => import('./pages/PublicProspectPage'));
const BecomeProspectPage = React.lazy(() => import('./pages/BecomeProspectPage'));
const ProspectsPage = React.lazy(() => import('./pages/ProspectsPage'));
const ProspectProfilePage = React.lazy(() => import('./pages/ProspectProfilePage'));
const DraftPoolPage = React.lazy(() => import('./pages/DraftPoolPage'));
const TeamsPage = React.lazy(() => import('./pages/TeamsPage'));
const TeamDetailPage = React.lazy(() => import('./pages/TeamDetailPage'));
const GamesPage = React.lazy(() => import('./pages/GamesPage'));
const GameDetailPage = React.lazy(() => import('./pages/GameDetailPage'));
const LeaderboardsPage = React.lazy(() => import('./pages/LeaderboardsPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const AthleteDashboard = React.lazy(() => import('./pages/AthleteDashboard'));
const AthleteIntake = React.lazy(() => import('./pages/AthleteIntake'));
const FanDashboard = React.lazy(() => import('./pages/FanDashboard'));
// FE-15 — MemberDashboard retired. TeamCommandCenter is the canonical
// Member landing (see /member + /member-home + preview alias below).
// AdminDashboard component is now only rendered via LAProspects (League
// Admin > Prospects) — there's no longer a separate /team-admin import here.
const ComparePage = React.lazy(() => import('./pages/ComparePage'));
const MockDraftPage = React.lazy(() => import('./pages/MockDraftPage'));
const DraftDayPage = React.lazy(() => import('./pages/DraftDayPage'));
const DraftEventDetailPage = React.lazy(() => import('./pages/DraftEventDetailPage'));
const TradeCenterPage = React.lazy(() => import('./pages/TradeCenterPage'));
const TeamSelectionPage = React.lazy(() => import('./pages/TeamSelectionPage'));
const MemberHubPage = React.lazy(() => import('./pages/MemberHubPage'));
const TeamCommandCenter = React.lazy(() => import('./pages/TeamCommandCenter'));
const MemberCommunityPage = React.lazy(() => import('./pages/MemberCommunityPage'));
const MemberMoneyPotPage = React.lazy(() => import('./pages/MemberMoneyPotPage'));
const FranchiseLevelsPage = React.lazy(() => import('./pages/FranchiseLevelsPage'));
const MemberLeagueComparison = React.lazy(() => import('./pages/MemberLeagueComparison'));
const MemberBadgeCenter = React.lazy(() => import('./pages/MemberBadgeCenter'));
const MemberContentDetailPage = React.lazy(() => import('./pages/MemberContentDetailPage'));
const MemberLegacyProfile = React.lazy(() => import('./pages/MemberLegacyProfile'));
const TeamHallOfBuilders = React.lazy(() => import('./pages/TeamHallOfBuilders'));
const TeamHistoryTimeline = React.lazy(() => import('./pages/TeamHistoryTimeline'));
const DiscussionDetailPage = React.lazy(() => import('./pages/DiscussionDetailPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const DisclaimerPage = React.lazy(() => import('./pages/DisclaimerPage'));
const CommunityGuidelinesPage = React.lazy(() => import('./pages/CommunityGuidelinesPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const DeactivatedPage = React.lazy(() => import('./pages/DeactivatedPage'));
const AccessDeniedPage = React.lazy(() => import('./pages/AccessDeniedPage'));

// League Admin — layout stays static (wraps every LA route), children are lazy.
import { LeagueAdminLayout } from './components/layout/LeagueAdminLayout';
const LAOverview = React.lazy(() => import('./pages/league-admin/LAOverview'));
const LAGames = React.lazy(() => import('./pages/league-admin/LAGames'));
const LATeams = React.lazy(() => import('./pages/league-admin/LATeams'));
const LATeamDetail = React.lazy(() => import('./pages/league-admin/LATeamDetail'));
const LATravel = React.lazy(() => import('./pages/league-admin/LATravel'));
const LAExpenses = React.lazy(() => import('./pages/league-admin/LAExpenses'));
const LATasks = React.lazy(() => import('./pages/league-admin/LATasks'));
const LAMarketing = React.lazy(() => import('./pages/league-admin/LAMarketing'));
const LACommunity = React.lazy(() => import('./pages/league-admin/LACommunity'));
const LAIT = React.lazy(() => import('./pages/league-admin/LAIT'));
const LAContent = React.lazy(() => import('./pages/league-admin/LAContent'));
const LAReports = React.lazy(() => import('./pages/league-admin/LAReports'));
const LAAdminUsers = React.lazy(() => import('./pages/league-admin/LAAdminUsers'));
const LAUsers = React.lazy(() => import('./pages/league-admin/LAUsers'));
const StaffTeamHubPicker = React.lazy(() => import('./pages/staff/StaffTeamHubPicker'));
const LAMemberContent = React.lazy(() => import('./pages/league-admin/LAMemberContent'));
const LAMemberWaitlist = React.lazy(() => import('./pages/league-admin/LAMemberWaitlist'));
const LAVoting = React.lazy(() => import('./pages/league-admin/LAVoting'));
const LANotifications = React.lazy(() => import('./pages/league-admin/LANotifications'));
const LAProspects = React.lazy(() => import('./pages/league-admin/LAProspects'));
const LAProspectReview = React.lazy(() => import('./pages/league-admin/LAProspectReview'));
const LAMoneyPot = React.lazy(() => import('./pages/league-admin/LAMoneyPot'));
const LAMoneyPotCommandCenter = React.lazy(() => import('./pages/league-admin/LAMoneyPotCommandCenter'));
const LAMoneyPotProjects = React.lazy(() => import('./pages/league-admin/LAMoneyPotProjects'));
const LADiscussions = React.lazy(() => import('./pages/league-admin/LADiscussions'));
const NotificationCenterPage = React.lazy(() => import('./pages/NotificationCenterPage'));
const NotificationSettingsPage = React.lazy(() => import('./pages/NotificationSettingsPage'));
const ForumsPage = React.lazy(() => import('./pages/ForumsPage'));
const RankingsPage = React.lazy(() => import('./pages/RankingsPage'));
const ScoutingReportForm = React.lazy(() => import('./pages/admin/ScoutingReportForm'));
const MembershipPage = React.lazy(() => import('./pages/MembershipPage'));
const BecomeMemberPage = React.lazy(() => import('./pages/BecomeMemberPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));

// Role groupings for route access
const ADMIN_ROLES = [ROLES.FRONT_OFFICE_STAFF, ROLES.TEAM_ADMIN];
const ALL_ROLES = [ROLES.MEMBER, ROLES.PROSPECT, ROLES.TEAM_ACTIVE_ATHLETE, ROLES.TEAM_ADMIN, ROLES.FRONT_OFFICE_STAFF];
const MEMBER_ONLY_ROLES = [ROLES.MEMBER, ROLES.TEAM_ACTIVE_ATHLETE, ROLES.TEAM_ADMIN, ROLES.FRONT_OFFICE_STAFF];
const MEMBER_LEVEL_ROLES = [ROLES.MEMBER, ROLES.TEAM_ACTIVE_ATHLETE];

// Dashboard routes per role. The authoritative map now lives in
// `src/lib/roles.js` (`ROLES` + `ROLE_DASHBOARDS`) so App.js, page
// guards, tests, and integration modules all read from a single
// source of truth for role slugs.

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Phase 7AA — block deactivated/suspended accounts from any protected
  // route. This is a UX layer; the backend independently rejects every
  // authenticated request from a non-active user with 403.
  if (user && user.account_status && user.account_status !== 'active') {
    return <Navigate to="/deactivated" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Send unauthorized users to the dedicated Access Denied page
    // instead of silently redirecting to their own dashboard. The
    // page still surfaces a "Back to My Dashboard" CTA that resolves
    // via `dashboardForRole(user.role)`, so the recovery path is
    // preserved — the difference is that the user now sees an
    // explanation rather than a silent bounce.
    return (
      <Navigate
        to="/access-denied"
        replace
        state={{ from: location.pathname, requiredRoles: allowedRoles }}
      />
    );
  }

  return children;
};

// Pro Member Route — requires auth + Pro membership (admin roles bypass)
const ProRoute = ({ children, feature }) => {
  const { user, loading, isAuthenticated, isPro } = useAuth();

  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isPro) return <ProGate feature={feature} />;
  return children;
};

// Home Route — public homepage for unauthenticated visitors. Phase 11:
// authenticated members and prospects are redirected to their role
// dashboard so they never see the marketing homepage while signed in.
// Other authenticated roles (team_active_athlete, team_admin,
// front_office_staff) can still view the homepage if they navigate to
// it directly (admin staff often use it as a quick public-facing QA
// surface).
const HomeRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) return <PageLoader />;
  if (isAuthenticated && user) {
    // Deactivated users go to /deactivated regardless of role.
    if (user.account_status && user.account_status !== 'active') {
      return <Navigate to="/deactivated" replace />;
    }
    if (user.role === 'member') {
      return <Navigate to="/member" replace />;
    }
    if (user.role === 'prospect') {
      return <Navigate to="/prospect" replace />;
    }
  }
  return children;
};

// Layout wrapper for pages with navbar/footer
const Layout = ({ children, showFooter = true }) => {
  const location = useLocation();
  // FE-2B — Federation Sports atmosphere. Mounted only on Member
  // surfaces so Front Office / League Admin / Prospect / Athlete
  // flows keep their current chrome untouched. Purely decorative:
  // fixed / pointer-events-none / z-index -1.
  const isMemberSurface =
    location.pathname === '/member' ||
    location.pathname.startsWith('/member/') ||
    location.pathname === '/member-home';
  return (
    <div className="flex flex-col min-h-screen">
      {isMemberSurface && <MemberAtmosphere />}
      <Navbar />
      <StaffPreviewBanner />
      <main className="flex-1 pb-10">{children}</main>
      {showFooter && <Footer />}
      <LiveTicker />
    </div>
  );
};

function AppRoutes() {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Lock prospects to /prospect only — redirect from any other route
  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated || user?.role !== 'prospect') return;
    const path = location.pathname;
    const isAuthRoute =
      path === '/login' ||
      path === '/register' ||
      path === '/forgot-password' ||
      path === '/reset-password';
    // Public vanity share page — prospects are allowed to view other athletes' profiles.
    const isPublicShareRoute = path.startsWith('/m/');
    // Public legal pages — prospects can view privacy/terms/disclaimer/community guidelines without being redirected.
    const isPublicLegalRoute =
      path === '/privacy-policy' ||
      path === '/terms' ||
      path === '/disclaimer' ||
      path === '/community-guidelines';
    if (path !== '/prospect' && path !== '/prospect/intake' && !isAuthRoute && !isPublicShareRoute && !isPublicLegalRoute) {
      navigate('/prospect', { replace: true });
    }
  }, [user, isAuthenticated, loading, location.pathname, navigate]);

  return (
    <React.Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomeRoute><Layout><HomePage /></Layout></HomeRoute>} />
        <Route path="/become-a-prospect" element={<BecomeProspectPage />} />
        <Route path="/privacy-policy" element={<Layout><PrivacyPolicyPage /></Layout>} />
        <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
        <Route path="/disclaimer" element={<Layout><DisclaimerPage /></Layout>} />
      <Route path="/community-guidelines" element={<Layout><CommunityGuidelinesPage /></Layout>} />
      <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
      <Route path="/deactivated" element={<DeactivatedPage />} />
      <Route path="/access-denied" element={<Layout><AccessDeniedPage /></Layout>} />
      <Route path="/m/:slug" element={<PublicProspectPage />} />
      <Route path="/prospects" element={<Layout><ProspectsPage /></Layout>} />
      <Route path="/prospects/:id" element={<Layout><ProspectProfilePage /></Layout>} />
      <Route path="/draft-pool" element={<Layout><DraftPoolPage /></Layout>} />
      <Route path="/teams" element={<Layout><TeamsPage /></Layout>} />
      <Route path="/teams/:id" element={<Layout><TeamDetailPage /></Layout>} />
      <Route path="/games" element={<Layout><GamesPage /></Layout>} />
      <Route path="/games/:id" element={<Layout><GameDetailPage /></Layout>} />
      <Route path="/leaderboards" element={<Layout><LeaderboardsPage /></Layout>} />
      <Route path="/compare" element={<Layout><ComparePage /></Layout>} />
      <Route path="/rankings" element={<Layout><RankingsPage /></Layout>} />
      <Route path="/mock-drafts" element={<Layout><MockDraftPage /></Layout>} />
      <Route path="/membership" element={<Layout><MembershipPage /></Layout>} />
      <Route path="/membership/success" element={<Layout><MembershipPage /></Layout>} />
      {/* Phase 1 — public Founding Member landing (12-team membership).
          Intentionally separate from the paid /membership Stripe page. */}
      <Route path="/become-a-member" element={<Layout><BecomeMemberPage /></Layout>} />
      <Route path="/draft-day" element={<Layout showFooter={false}><DraftDayPage /></Layout>} />
      <Route path="/draft-day/:id" element={<Layout showFooter={false}><DraftEventDetailPage /></Layout>} />
      
      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* ===================== ROLE-BASED DASHBOARDS ===================== */}

      {/* Member Dashboard (member role).
          Phase 8.4 — also accessible to ADMIN_ROLES via permission-based
          Staff Preview Access. Backend `require_pro_member` already
          bypasses ADMIN_ROLES, so staff don't need a Pro subscription.
          Phase 9 Phase 2 — `/member-home` now renders the full
          MemberDashboard (selected team, Pro access, Team Hub link,
          team polls, etc.) instead of the legacy FanDashboard. The
          dashboard auto-redirects to /select-team when no team is set. */}
      <Route
        path="/member-home"
        element={
          <ProtectedRoute allowedRoles={['member', ...ADMIN_ROLES]}>
            <Layout><TeamCommandCenter /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Prospect Dashboard (prospect role) — standalone (no global Layout/Navbar) */}
      <Route
        path="/prospect"
        element={
          <ProtectedRoute allowedRoles={['prospect']}>
            <AthleteDashboard />
          </ProtectedRoute>
        }
      />

      {/* Phase 1A — Athlete Intelligence Intake (prospect only) */}
      <Route
        path="/prospect/intake"
        element={
          <ProtectedRoute allowedRoles={['prospect']}>
            <AthleteIntake />
          </ProtectedRoute>
        }
      />

      {/* Athlete Dashboard (team_active_athlete role) */}
      <Route
        path="/athlete"
        element={
          <ProtectedRoute allowedRoles={['team_active_athlete']}>
            <Layout><FanDashboard /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Legacy /team-admin route — consolidated into League Admin so
          there is exactly one controlling admin area. Both ADMIN_ROLES
          (front_office_staff + team_admin) reach the same unified page;
          prospects/members are blocked by ProtectedRoute. The
          AdminDashboard component still hides FO-only actions
          (Deny / Bulk Approve / Bulk Hide / Bulk Show) from team_admin
          via its internal `user?.role === 'front_office_staff'` checks
          — same gating, single UI source of truth. */}
      <Route
        path="/team-admin"
        element={
          <ProtectedRoute allowedRoles={ADMIN_ROLES}>
            <Navigate to="/league-admin/prospects" replace />
          </ProtectedRoute>
        }
      />

      {/* Admin Trades */}
      <Route
        path="/admin/trades"
        element={
          <ProtectedRoute allowedRoles={ADMIN_ROLES}>
            <Layout><TradeCenterPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin Scouting */}
      <Route
        path="/admin/scouting/:prospectId"
        element={
          <ProtectedRoute allowedRoles={ADMIN_ROLES}>
            <Layout><ScoutingReportForm /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/scouting/:prospectId/:reportId"
        element={
          <ProtectedRoute allowedRoles={ADMIN_ROLES}>
            <Layout><ScoutingReportForm /></Layout>
          </ProtectedRoute>
        }
      />

      {/* ===================== MEMBER FEATURES ===================== */}

      {/* Team Selection — members & admins only, NOT prospects */}
      <Route
        path="/select-team"
        element={
          <ProtectedRoute allowedRoles={[...MEMBER_LEVEL_ROLES, ...ADMIN_ROLES]}>
            <Layout><TeamSelectionPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Pro Member Dashboard — members & admins only, NOT prospects.
        MEMBER-FREE-ACCESS-1: ProRoute wrapper removed — any registered
        member with a selected team gets the full experience. */}
      {/* FE-15 — /member cutover. Canonical Member landing is now
          TeamCommandCenter. Legacy MemberDashboard retired. The
          /member/command-center-preview route below remains as a
          temporary alias for verification. */}
      <Route
        path="/member"
        element={
          <ProtectedRoute allowedRoles={MEMBER_ONLY_ROLES}>
            <Layout>
              <TeamCommandCenter />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Team Hub (Voting) — members & admins only, NOT prospects.
        MEMBER-FREE-ACCESS-1: ProRoute wrapper removed. */}
      <Route
        path="/member/hub/:teamId"
        element={
          <ProtectedRoute allowedRoles={MEMBER_ONLY_ROLES}>
            <Layout>
              <MemberHubPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* TEAM-COMMAND-CENTER-1B-FE-2 — temporary preview alias.
         Member-only. Kept as an alias to /member (which now renders
         TeamCommandCenter after FE-15) during verification. Safe to
         retire in a future ticket once no external links point here. */}
      <Route
        path="/member/command-center-preview"
        element={
          <ProtectedRoute allowedRoles={MEMBER_ONLY_ROLES}>
            <Layout>
              <TeamCommandCenter />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* TEAM-COMMAND-CENTER-1B-FE-5 — permanent Team Community
         destination (Discussions + Voting). Additive route; does
         NOT retire /member/hub/:teamId. */}
      <Route
        path="/member/community/:teamId"
        element={
          <ProtectedRoute allowedRoles={MEMBER_ONLY_ROLES}>
            <Layout>
              <MemberCommunityPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* TEAM-COMMAND-CENTER-1B-FE-8 — permanent Member Money Pot
         detail destination. Reuses the existing MoneyPotPanel;
         Member read-only. Additive route; does NOT retire the
         legacy Hub Money Pot section. */}
      <Route
        path="/member/money-pot/:teamId"
        element={
          <ProtectedRoute allowedRoles={MEMBER_ONLY_ROLES}>
            <Layout>
              <MemberMoneyPotPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Franchise Levels Roadmap — members & admins only.
        MEMBER-FREE-ACCESS-1: ProRoute wrapper removed. */}
      <Route
        path="/member/franchise/:teamId"
        element={
          <ProtectedRoute allowedRoles={MEMBER_ONLY_ROLES}>
            <Layout>
              <FranchiseLevelsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* MP-8 — League Comparison Center (members & admins) */}
      <Route
        path="/member/league"
        element={
          <ProtectedRoute allowedRoles={MEMBER_ONLY_ROLES}>
            <Layout>
              <MemberLeagueComparison />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* MP-9A — Badge Center (members & admins) */}
      <Route
        path="/member/badges"
        element={
          <ProtectedRoute allowedRoles={MEMBER_ONLY_ROLES}>
            <Layout>
              <MemberBadgeCenter />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* MEM-BADGE-1C / 1D — Authoritative Member content detail. */}
      <Route
        path="/member/content/:contentId"
        element={
          <ProtectedRoute allowedRoles={MEMBER_ONLY_ROLES}>
            <Layout>
              <MemberContentDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* MP-11 — Legacy Profile, Hall of Builders, Team History */}
      <Route
        path="/member/legacy"
        element={
          <ProtectedRoute allowedRoles={MEMBER_ONLY_ROLES}>
            <Layout>
              <MemberLegacyProfile />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:teamId/hall-of-builders"
        element={
          <ProtectedRoute allowedRoles={MEMBER_ONLY_ROLES}>
            <Layout>
              <TeamHallOfBuilders />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:teamId/team-history"
        element={
          <ProtectedRoute allowedRoles={MEMBER_ONLY_ROLES}>
            <Layout>
              <TeamHistoryTimeline />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Discussion Detail — members & admins only, NOT prospects */}
      <Route
        path="/member/discussion/:discId"
        element={
          <ProtectedRoute allowedRoles={MEMBER_ONLY_ROLES}>
            <Layout><DiscussionDetailPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* ===================== COMMON AUTHENTICATED ===================== */}

      {/* Notifications */}
      <Route
        path="/notifications"
        element={
          <ProtectedRoute allowedRoles={ALL_ROLES}>
            <Layout><NotificationCenterPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/settings"
        element={
          <ProtectedRoute allowedRoles={ALL_ROLES}>
            <Layout><NotificationSettingsPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Forums */}
      <Route
        path="/forums"
        element={
          <ProtectedRoute allowedRoles={ALL_ROLES}>
            <Layout><ForumsPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* ===================== LEAGUE ADMIN (sole admin area) =====================
          Open to ADMIN_ROLES (front_office_staff + team_admin). Internal
          AdminDashboard checks still gate FO-only actions like Deny,
          Bulk Approve/Deny, and Bulk Hide/Make-Visible to FO staff. */}
      <Route
        path="/league-admin"
        element={
          <ProtectedRoute allowedRoles={ADMIN_ROLES}>
            <LeagueAdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<LAOverview />} />
        <Route path="prospects" element={<LAProspects />} />
        <Route path="prospects/:id" element={<LAProspectReview />} />
        <Route path="money-pot" element={<LAMoneyPot />} />
        <Route path="money-pot/command" element={<LAMoneyPotCommandCenter />} />
        <Route path="money-pot/projects" element={<LAMoneyPotProjects />} />
        <Route path="games" element={<LAGames />} />
        <Route path="teams" element={<LATeams />} />
        <Route path="teams/:teamId" element={<LATeamDetail />} />
        <Route path="travel" element={<LATravel />} />
        <Route path="expenses" element={<LAExpenses />} />
        <Route path="tasks" element={<LATasks />} />
        <Route path="marketing" element={<LAMarketing />} />
        <Route path="community" element={<LACommunity />} />
        <Route path="it" element={<LAIT />} />
        <Route path="content" element={<LAContent />} />
        <Route path="discussions" element={<LADiscussions />} />
        <Route path="reports" element={<LAReports />} />
        <Route path="admin-users" element={<LAAdminUsers />} />
        <Route path="users" element={<LAUsers />} />
        <Route path="member-content" element={<LAMemberContent />} />
        <Route path="member-waitlist" element={<LAMemberWaitlist />} />
        <Route path="voting" element={<LAVoting />} />
        <Route path="notifications" element={<LANotifications />} />
        {/* Phase 8.4.1 — Team Hub Preview picker (admin-only) */}
        <Route path="team-hub-preview" element={<StaffTeamHubPicker />} />
      </Route>

      {/* Legacy redirects — consolidate every old admin URL into League Admin */}
      <Route path="/fan" element={<Navigate to="/member-home" replace />} />
      <Route path="/admin" element={<Navigate to="/league-admin/prospects" replace />} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BadgeCelebrationsProvider>
          <div className="App">
            <AppRoutes />
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: 'hsl(240 10% 7%)',
                  border: '1px solid hsl(240 5% 17%)',
                  color: 'hsl(0 0% 98%)',
                },
              }}
            />
          </div>
        </BadgeCelebrationsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
